'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { useUser, useClerk, SignInButton } from '@clerk/nextjs';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import {
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
  ChevronDown,
  MessageCircleHeart,
  VerifiedIcon,
  UserCog,
  HousePlus,
  Wallet,
  CreditCard,
  CalendarIcon,
  Home,
  BadgeXIcon,
  HomeIcon,
  Compass,
  BookOpenCheck,
  Settings,
  ToggleRight,
  MegaphoneIcon,
} from 'lucide-react';
import { Button } from '../../ui/button';
import type { ButtonProps } from '../../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { NavbarCurrencySwitcher } from '../Utilities/NavbarCurrencySwitcher';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

// Create a new UserButtonandMenu component and move the old return into this
const UserButtonAndMenu = ({
  isHosting,
  onRoleChange,
  color,
}: {
  isHosting?: boolean;
  onRoleChange?: (_isHost: boolean) => void;
  color: 'black' | 'white';
}) => {
  const { signOut, openUserProfile } = useClerk();
  const router = useRouter();
  const pathname = usePathname();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata.role === 'admin';

  // Default to handling the role internally if no handler is provided
  const handleRoleChange = (isHost: boolean) => {
    // Call the external handler if provided
    if (onRoleChange) {
      onRoleChange(isHost);
    }

    // Redirect to appropriate page based on role
    router.push(isHost ? '/host' : '/');
  };

  return (
    <div
      className={cn(
        'flex items-center rounded-full px-4 py-1 border-2 shadow-sm justify-center transition-colors duration-300',
        color === 'black' ? 'text-primary border-primary' : 'text-white border-white'
      )}
    >
      {isHosting !== true && <NavbarCurrencySwitcher variant="ghost" color={color} />}
      {isHosting !== true && (
        <Separator
          orientation="vertical"
          className={cn(
            'h-8 font-bold mr-3 ml-1 transition-colors duration-300',
            color === 'black' ? 'bg-primary' : 'bg-white'
          )}
        />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="flex items-center gap-2">
            {user?.hasImage ? (
              <div
                className={cn(
                  'w-8 h-8 text-xs text-bold drop-shadow-sm rounded-full transition-colors duration-300',
                  color === 'black' ? 'border-2 border-primary' : 'border-2 border-white'
                )}
              >
                <img
                  src={user?.imageUrl}
                  alt={`${user?.firstName} ${user?.lastName}`}
                  className="object-cover w-full h-full rounded-full"
                  referrerPolicy="no-referrer"
                />
              </div>
            ) : (
              <>
                {/* Fallback to initials if there is no profile image */}
                <Avatar
                  className={cn(
                    'w-8 h-8 text-xs text-bold drop-shadow-sm transition-colors duration-300',
                    color === 'black' ? 'border-2 border-primary' : 'border-2 border-white'
                  )}
                >
                  <AvatarImage src={user?.imageUrl} />
                  <AvatarFallback>
                    {' '}
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              </>
            )}
            <span className="text-sm uppercase font-extrabold hidden md:block">
              {user?.firstName}
            </span>

            <ChevronDown className="w-4 h-4" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent className="mt-4 w-60 rounded-xl border border-border px-2 py-2 text-primary drop-shadow-2xl">
            {/* Quick Mode Switch */}
            <DropdownMenuLabel className="text-sm">Logged in as Guest</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleRoleChange(!isHosting)} className="mb-1">
              <ToggleRight className="mr-2 h-4 w-4" />
              <span>Switch to {isHosting ? 'Guest' : 'Host'} Mode</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Menu Links Section */}
            <DropdownMenuGroup className="py-1">
              <DropdownMenuItem asChild className="hover:bg-primary hover:text-primary-foreground">
                <Link href="/explore" className={pathname.includes('/explore') ? 'font-black' : ''}>
                  <Compass className="mr-2 h-4 w-4" />
                  Explore Properties
                </Link>
              </DropdownMenuItem>

              {/* Guest-specific navigation */}
              {!isHosting && (
                <>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/bookings"
                      className={pathname.includes('/bookings') ? 'font-black' : ''}
                    >
                      <BookOpenCheck className="mr-2 h-4 w-4" />
                      My Bookings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/chat">
                      <MessageCircleHeart className="mr-2 h-4 w-4" />
                      Messages
                    </Link>
                  </DropdownMenuItem>
                </>
              )}

              {/* Host-specific navigation */}
              {isHosting && (
                <>
                  <DropdownMenuItem asChild>
                    <Link href="/host" className={pathname === '/host' ? 'font-black' : ''}>
                      <Home className="mr-2 h-4 w-4" />
                      Control Hub
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/host/properties"
                      className={pathname === '/host/properties' ? 'font-black' : ''}
                    >
                      <HomeIcon className="mr-2 h-4 w-4" />
                      My Properties
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/host/bookings"
                      className={pathname.includes('/host/bookings') ? 'font-black' : ''}
                    >
                      <BookOpenCheck className="mr-2 h-4 w-4" />
                      Manage Bookings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/host/wallet">
                      <Wallet className="mr-2 h-4 w-4" />
                      Finances & Payouts
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/chat">
                      <MessageCircleHeart className="mr-2 h-4 w-4" />
                      Messages
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            {/* Account Settings Section */}
            <DropdownMenuLabel className="text-sm">Account</DropdownMenuLabel>
            <DropdownMenuGroup className="py-1">
              <DropdownMenuItem onClick={() => openUserProfile()}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Profile Settings</span>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/user/verification">
                  {user?.publicMetadata?.isVerified === 'verified' ? (
                    <VerifiedIcon className="h-4 w-4 text-brand-blue mr-2" />
                  ) : (
                    <BadgeXIcon className="h-4 w-4 text-red-600 mr-2"></BadgeXIcon>
                  )}
                  Verification Status
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>

            {isAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-sm">Admin</DropdownMenuLabel>
                <DropdownMenuGroup className="py-1">
                  <DropdownMenuItem asChild>
                    <Link href="/admin/users">
                      <UserCog className="mr-2 h-4 w-4" />
                      <span>Manage Users</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/admin/properties">
                      <HousePlus className="mr-2 h-4 w-4" />
                      Manage Properties
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/admin/campaigns">
                      <MegaphoneIcon className="mr-2 h-4 w-4" />
                      Manage Campaigns
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/admin/bookings">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      Manage Bookings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/admin/refunds">
                      <CreditCard className="mr-2 h-4 w-4" />
                      Manage Refunds
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/admin/payouts">
                      <CreditCard className="mr-2 h-4 w-4" />
                      Manage Payouts
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </>
            )}

            <DropdownMenuSeparator className="my-1 h-px bg-border" />
            <DropdownMenuItem onClick={() => signOut(() => router.push('/'))}>
              <ArrowLeftCircleIcon className="mr-2 h-4 w-4" /> <span>Sign Out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
    </div>
  );
};

// Refactor to show the default <SignInButton /> if the user is logged out
// Show the UserButtonAndMenu if the user is logged in
export const UserButton = ({
  buttonText = 'Sign In',
  variant = 'default',
  isHosting,
  onRoleChange,
  color,
}: {
  buttonText?: string;
  variant?: ButtonProps['variant'];
  isHosting?: boolean;
  onRoleChange?: (_: boolean) => void;
  color: 'black' | 'white';
}) => {
  const { isLoaded, user } = useUser();
  const pathname = usePathname();
  const queryParams = useSearchParams();
  const redirectURL = `${process.env.NEXT_PUBLIC_DOMAIN}${pathname}?${queryParams.toString()}`;

  if (!isLoaded || !user?.id)
    return (
      <SignInButton signUpForceRedirectUrl={redirectURL} forceRedirectUrl={redirectURL}>
        <Button variant={variant} className={buttonText == 'Sign In' ? '' : 'w-full'}>
          {buttonText}
          <ArrowRightCircleIcon className="ml-2 h-4 w-4" />
        </Button>
      </SignInButton>
    );

  return <UserButtonAndMenu isHosting={isHosting} onRoleChange={onRoleChange} color={color} />;
};
